import type { ReactNode, CSSProperties } from 'react';

type Spacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type Columns = 1 | 2 | 3 | 4 | 5 | 6 | 12 | 'auto';

interface GridProps {
  children: ReactNode;
  /**
   * Number of columns
   * @default 'auto'
   */
  columns?: Columns;
  /**
   * Gap between grid items
   * @default 'md'
   */
  gap?: Spacing;
  /**
   * Row gap (overrides gap for rows)
   */
  rowGap?: Spacing;
  /**
   * Column gap (overrides gap for columns)
   */
  columnGap?: Spacing;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

interface GridItemProps {
  children: ReactNode;
  /**
   * Number of columns to span
   * @default 1
   */
  span?: number;
  /**
   * Column start position
   */
  start?: number;
  /**
   * Additional CSS class
   */
  className?: string;
}

const spacingMap: Record<Spacing, string> = {
  none: '0',
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
};

/**
 * Grid component for grid-based layouts.
 *
 * @example
 * <Grid columns={3} gap="lg">
 *   <GridItem>Item 1</GridItem>
 *   <GridItem span={2}>Wide Item</GridItem>
 *   <GridItem>Item 3</GridItem>
 * </Grid>
 */
export function Grid({
  children,
  columns = 'auto',
  gap = 'md',
  rowGap,
  columnGap,
  className = '',
  'data-testid': testId,
}: GridProps) {
  const style: CSSProperties = {
    display: 'grid',
    gridTemplateColumns:
      columns === 'auto' ? 'repeat(auto-fit, minmax(250px, 1fr))' : `repeat(${columns}, 1fr)`,
    gap: spacingMap[gap],
    ...(rowGap && { rowGap: spacingMap[rowGap] }),
    ...(columnGap && { columnGap: spacingMap[columnGap] }),
  };

  return (
    <div className={className} style={style} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * Grid item component for controlling span and position.
 */
export function GridItem({ children, span = 1, start, className = '' }: GridItemProps) {
  const style: CSSProperties = {
    gridColumn: start ? `${start} / span ${span}` : `span ${span}`,
  };

  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}
